import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { verifyJwtToken } from "../shared/_services/common.services";
import { readCurrentUser } from "../shared/_helpers/currentUser";
import { connectSocket, disconnectSocket } from "../shared/_helpers/socket";

const AuthContext = createContext(undefined);

// SEC-066: role/identity gating must not trust the localStorage `currentUser`
// object, because it is plain JSON a user can edit in DevTools and reload.
// This re-derives the role from the server on every app load and is the one
// place role verification lives. `status` distinguishes "we haven't checked
// yet" from "checked, and there's no valid session" so a consumer never has
// to guess whether `role` is null because nobody's home yet or because
// verification is still in flight.
//
// Deliberately POST /users/verify-jwttoken, not GET /users/:userId. An
// earlier version called getProfile(stored.user._id) — the id the *client*
// names, not the id the token belongs to. requireSelfOrAdmin on that route
// blocks an ordinary client from pointing it at someone else, but it
// explicitly allows an admin to read any user's profile — so an admin (not
// super-admin) could edit `_id` to a known super-admin's real id and have
// this context adopt that account's role, cosmetically unlocking
// super-admin-only UI (caught in review on #78). verify-jwttoken takes no id
// at all: the server decodes the token itself and looks up whoever it was
// issued to, so there is nothing here for editable JSON to redirect.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // 'loading' | 'verified' | 'unauthenticated'
  // Guards against the mount-time verify() and a post-login refetch()
  // resolving out of order (flagged in review on #78): if the app boots with
  // an old token already stored, that request can still be in flight when a
  // fresh login mints a new one and calls refetch(). Without this, whichever
  // response lands last wins even if it started first, so a slow, stale
  // response for the old token could overwrite the freshly verified
  // identity. Only the response belonging to the most recently *issued* call
  // is ever committed.
  const latestRequestId = useRef(0);

  const verify = useCallback(async () => {
    const stored = readCurrentUser();
    if (!stored?.token) {
      latestRequestId.current += 1;
      setUser(null);
      setStatus("unauthenticated");
      disconnectSocket();
      return;
    }

    const requestId = ++latestRequestId.current;
    setStatus("loading");
    const res = await verifyJwtToken(stored.token);
    if (requestId !== latestRequestId.current) {
      // A newer call has started since this one was issued — its result, not
      // this one, gets to decide the current identity.
      return;
    }
    if (res?.status === 200 && res?.data?.user) {
      setUser(res.data.user);
      setStatus("verified");
      // #87 / SEC-085: the socket opens only for a server-verified session.
      // A no-op if it is already open for this account; reconnects as the
      // new account on a user switch.
      connectSocket();
    } else {
      // Fails closed: a network blip and an actually-invalid session are
      // indistinguishable here, and granting a role on the strength of "the
      // check didn't come back" is exactly the trust SEC-066 is closing.
      setUser(null);
      setStatus("unauthenticated");
      disconnectSocket();
    }
  }, []);

  useEffect(() => {
    verify();
  }, [verify]);

  return (
    <AuthContext.Provider value={{ user, role: user?.userType ?? null, status, refetch: verify }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
